import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { groupTypeDotColor } from './groupTypeStyles';
import GroupsTypeBadge from './GroupsTypeBadge';
import { periodLabel } from '../../utils/orgGroupTree';
import { groupHasPermanentPeriod, isPermanentPeriod } from '../../constants/groupPeriods';

const TREE_LAYOUT_GAP = 24;
const TREE_CARD_LANE_MIN = 200;
/** Show-all add ghosts — narrow lane so they hug the stem, not the far edge. */
const MINI_GHOST_LANE = 76;
const MIN_ZOOM = 0.18;
const MAX_ZOOM = 2.25;
const CANVAS_PAD = 36;
const FOCUS_ZOOM = 0.92;
const CAMERA_TRANSITION_MS = 320;

function ghostLaneWidth(node) {
  if (!node || node.nodeType !== 'ghost') return TREE_CARD_LANE_MIN;
  return node.mini ? MINI_GHOST_LANE : TREE_CARD_LANE_MIN;
}

function isPermanentPeriodWebNode(periodNode) {
  if (!periodNode || periodNode.nodeType !== 'period') return false;
  const p = periodNode.period;
  if (!p) return false;
  if (isPermanentPeriod(p)) return true;
  return isPermanentPeriod({
    id: p.period_id,
    title: p.period_title || p.title,
    code: p.period_code || p.code,
    is_permanent: p.is_permanent
  });
}

/** Sole Permanent child is folded into the group card (no separate period card). */
function getSolePermanentPeriodChild(groupNode) {
  if (!groupNode || groupNode.nodeType !== 'group') return null;
  const kids = groupNode.children || [];
  if (kids.length !== 1) return null;
  const only = kids[0];
  return isPermanentPeriodWebNode(only) ? only : null;
}

/** Folder drill-in: sole period under the browse root is Permanent. */
function getSolePermanentRootPeriod(treeChildren) {
  if (!Array.isArray(treeChildren) || treeChildren.length !== 1) return null;
  return isPermanentPeriodWebNode(treeChildren[0]) ? treeChildren[0] : null;
}

/**
 * Recursive lane width. Mini ghosts (Show all) add a narrow first-lane only at
 * this level; folder ghosts keep a full trailing lane on periods.
 *
 * Real nodes always reserve at least TREE_CARD_LANE_MIN so a 168px card cannot
 * overflow a mini-ghost-only child row (~76px) and overlap siblings.
 *
 * Permanent-merged groups skip the intermediate period lane and size from nested
 * teams (+ nested ghost) under that Permanent groups-period.
 */
function subtreeBranchMinWidth(node, ghostOpts = {}) {
  if (!node || node.nodeType === 'ghost') return ghostLaneWidth(node);

  // Permanent merge: group card → nested teams (period card omitted).
  if (ghostOpts.mergePermanent && node.nodeType === 'group') {
    const solePermanent = getSolePermanentPeriodChild(node);
    if (solePermanent) {
      const nested = solePermanent.children || [];
      const widths = nested.map((child) => subtreeBranchMinWidth(child, ghostOpts));
      if (ghostOpts.nestedTeam) widths.unshift(MINI_GHOST_LANE);
      if (widths.length === 0) return TREE_CARD_LANE_MIN;
      const childrenWidth =
        widths.reduce((a, b) => a + b, 0) + TREE_LAYOUT_GAP * Math.max(0, widths.length - 1);
      return Math.max(TREE_CARD_LANE_MIN, childrenWidth);
    }
  }

  const nested = node.children || [];
  let extraGhost = 0;
  let extraWidth = 0;
  let ghostFirst = false;
  if (ghostOpts.miniGhosts) {
    if (
      node.nodeType === 'group' &&
      ghostOpts.anotherPeriod &&
      !groupHasPermanentPeriod(node.group)
    ) {
      extraGhost = 1;
      extraWidth = MINI_GHOST_LANE;
      ghostFirst = true;
    } else if (node.nodeType === 'period' && ghostOpts.nestedTeam) {
      extraGhost = 1;
      extraWidth = MINI_GHOST_LANE;
      ghostFirst = true;
    }
  } else if (ghostOpts.rowGhosts && ghostOpts.nestedTeam && node.nodeType === 'period') {
    extraGhost = 1;
    extraWidth = TREE_CARD_LANE_MIN;
  }
  if (nested.length + extraGhost === 0) return TREE_CARD_LANE_MIN;
  const widths = nested.map((child) => subtreeBranchMinWidth(child, ghostOpts));
  if (extraGhost) {
    if (ghostFirst) widths.unshift(extraWidth);
    else widths.push(extraWidth);
  }
  const childrenWidth =
    widths.reduce((a, b) => a + b, 0) + TREE_LAYOUT_GAP * Math.max(0, widths.length - 1);
  return Math.max(TREE_CARD_LANE_MIN, childrenWidth);
}

function treeHLinePixels(childNodes, ghostOpts = {}) {
  const n = childNodes.length;
  if (n < 2) return null;
  const widths = childNodes.map((node) =>
    node.nodeType === 'ghost' ? ghostLaneWidth(node) : subtreeBranchMinWidth(node, ghostOpts)
  );
  const total = widths.reduce((a, b) => a + b, 0) + TREE_LAYOUT_GAP * (n - 1);
  const leftPx = widths[0] / 2;
  const widthPx = Math.max(0, total - widths[0] / 2 - widths[n - 1] / 2);
  return { left: `${leftPx}px`, width: `${widthPx}px` };
}

function OrgOwnerCrownIcon() {
  return (
    <span className="groups-org-owner-crown-emoji" aria-hidden>
      👑
    </span>
  );
}

function GroupTreeCard({
  node,
  isRoot = false,
  isOrganizationRoot = false,
  onSelectGroup,
  hoverId,
  setHoverId,
  showOrgOwnerCrown = false,
  onOpenOrgOwner,
  ownerCrownAriaLabel = 'Open owner team',
  ownerCrownTitle = 'Owner',
  isCameraFocus = false
}) {
  const isHover = hoverId === node.id;
  const name = node.name || node.group?.name;
  const stroke = isOrganizationRoot
    ? groupTypeDotColor(node.group_type || 'organization')
    : isHover || isCameraFocus
      ? groupTypeDotColor(node.group?.group_type || node.group_type)
      : '#cbd5e1';

  return (
    <div className="groups-org-tree-card-wrap" data-tree-node-id={node.id}>
      {isOrganizationRoot && showOrgOwnerCrown ? (
        <button
          type="button"
          className="groups-org-owner-crown-btn groups-org-owner-crown-btn--map"
          aria-label={ownerCrownAriaLabel}
          title={ownerCrownTitle}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOpenOrgOwner?.();
          }}
        >
          <OrgOwnerCrownIcon />
        </button>
      ) : null}
      <button
        type="button"
        className={`groups-org-tree-card${isRoot ? ' groups-org-tree-card--root' : ''}${
          isOrganizationRoot ? ' groups-org-tree-card--org-root' : ''
        }${isHover ? ' is-hover' : ''}${isCameraFocus ? ' is-camera-focus' : ''}`}
        style={
          isOrganizationRoot
            ? {
                background: groupTypeDotColor(node.group_type || 'organization'),
                borderColor: stroke
              }
            : { borderColor: stroke }
        }
        onMouseEnter={() => setHoverId(node.id)}
        onMouseLeave={() => setHoverId((h) => (h === node.id ? null : h))}
        onClick={() => {
          if (isOrganizationRoot) {
            onSelectGroup?.({ nodeType: 'organization', id: node.id, group: node });
            return;
          }
          if (!isRoot) onSelectGroup?.(node);
        }}
        title={
          isOrganizationRoot ? `${name} — fit map` : isRoot ? name : 'Open group'
        }      >
        <span className="groups-org-tree-card-name">{name}</span>
        <span className="groups-org-tree-card-meta">
          <GroupsTypeBadge
            groupType={
              node.group?.group_type || node.group_type || (isOrganizationRoot ? 'organization' : 'team')
            }
            displayLabel={node.group?.display_label || node.display_label}
            variant={isOrganizationRoot ? 'on-dark' : 'default'}
          />
        </span>
      </button>
    </div>
  );
}

/**
 * Ghost add-card — dashed placeholder rendered where the created node will
 * appear. Clicking it opens the create sheet with the matching mode
 * preselected (another period vs nested team), replacing the floating +.
 */
function GhostTreeCard({
  id,
  label,
  sublabel,
  onClick,
  hoverId,
  setHoverId,
  mini = false,
  tone = 'team'
}) {
  const isHover = hoverId === id;
  return (
    <button
      type="button"
      className={`groups-org-tree-card groups-org-tree-card--ghost groups-org-tree-card--ghost-${tone}${
        mini ? ' groups-org-tree-card--ghost-mini' : ''
      }${isHover ? ' is-hover' : ''}`}
      data-tree-node-id={id}
      onMouseEnter={() => setHoverId(id)}
      onMouseLeave={() => setHoverId((h) => (h === id ? null : h))}
      onClick={onClick}
      title={sublabel ? `${label} — ${sublabel}` : label}
    >
      <span className="groups-org-tree-card-name groups-org-tree-ghost-name">
        <span className="groups-org-tree-ghost-plus" aria-hidden>
          +
        </span>
        {mini ? null : <> {label}</>}
      </span>
      {mini ? (
        <span className="groups-org-tree-ghost-mini-label">{label}</span>
      ) : sublabel ? (
        <span className="groups-org-tree-card-meta">
          <span className="groups-org-tree-card-meta--period-text">{sublabel}</span>
        </span>
      ) : null}
    </button>
  );
}

function PeriodTreeCard({ node, onSelectPeriod, hoverId, setHoverId, isCameraFocus = false }) {
  const isHover = hoverId === node.id;
  const label = periodLabel(node.period);

  return (
    <button
      type="button"
      className={`groups-org-tree-card groups-org-tree-card--period${isHover ? ' is-hover' : ''}${
        isCameraFocus ? ' is-camera-focus' : ''
      }`}
      data-tree-node-id={node.id}
      onMouseEnter={() => setHoverId(node.id)}
      onMouseLeave={() => setHoverId((h) => (h === node.id ? null : h))}
      onClick={() => onSelectPeriod?.(node)}
      title="View members for this group-period"
    >
      <span className="groups-org-tree-card-name">{label}</span>
      <span className="groups-org-tree-card-meta">
        <GroupsTypeBadge groupType="period" />
        <span className="groups-org-tree-card-meta--period-text">{node.group?.name || 'Group'}</span>
      </span>
    </button>
  );
}

function DerivedGuardiansCard({ node, onSelectGroup, hoverId, setHoverId }) {
  const isHover = hoverId === node.id;
  const name = node.group?.name || 'Guardians';

  return (
    <button
      type="button"
      className={`groups-org-tree-card groups-org-tree-card--derived${isHover ? ' is-hover' : ''}`}
      data-tree-node-id={node.id}
      onMouseEnter={() => setHoverId(node.id)}
      onMouseLeave={() => setHoverId((h) => (h === node.id ? null : h))}
      onClick={() => onSelectGroup?.(node)}
      title="View guardians linked to enrolled students"
    >
      <span className="groups-org-tree-card-name">{name}</span>
      <span className="groups-org-tree-card-meta">
        <GroupsTypeBadge groupType="derived" />
        <span className="groups-org-tree-card-meta--period-text">Person links</span>
      </span>
    </button>
  );
}

function NestedGroupCard({
  node,
  onSelectGroup,
  hoverId,
  setHoverId,
  isCameraFocus = false,
  showAllHierarchy = false
}) {
  if (node.isDerivedGuardians) {
    return (
      <DerivedGuardiansCard
        node={node}
        onSelectGroup={onSelectGroup}
        hoverId={hoverId}
        setHoverId={setHoverId}
      />
    );
  }
  const isHover = hoverId === node.id;
  const group = node.group;
  const kind = group?.membership_kind === 'anchor' ? 'anchor' : 'extending';
  const kindTitle = showAllHierarchy
    ? kind === 'anchor'
      ? 'Anchor — zoom to this team'
      : 'Extending — zoom to this team'
    : kind === 'anchor'
      ? 'Anchor — show group periods'
      : 'Show group periods';

  return (
    <button
      type="button"
      className={`groups-org-tree-card groups-org-tree-card--nested groups-org-tree-card--${kind}${
        isHover ? ' is-hover' : ''
      }${isCameraFocus ? ' is-camera-focus' : ''}`}
      data-tree-node-id={node.id}
      title={kindTitle}
      onMouseEnter={() => setHoverId(node.id)}
      onMouseLeave={() => setHoverId((h) => (h === node.id ? null : h))}
      onClick={() => onSelectGroup?.(node)}
    >
      <span className="groups-org-tree-card-name">{group?.name}</span>
      <span className="groups-org-tree-card-meta">
        <GroupsTypeBadge
          groupType={group?.group_type || 'team'}
          displayLabel={group?.display_label}
        />
      </span>
    </button>
  );
}

/** Group + sole Permanent period as one card (opens members). */
function PermanentMergedGroupCard({
  groupNode,
  periodNode,
  onSelectPeriod,
  hoverId,
  setHoverId,
  isCameraFocus = false,
  isRoot = false,
  isOrganizationRoot = false
}) {
  const isHover = hoverId === groupNode.id;
  const group = groupNode.group;
  const kind = group?.membership_kind === 'anchor' ? 'anchor' : 'extending';
  const rootClass = isRoot
    ? ` groups-org-tree-card--root${isOrganizationRoot ? ' groups-org-tree-card--org-root' : ''}`
    : '';

  return (
    <button
      type="button"
      className={`groups-org-tree-card${
        isRoot ? '' : ' groups-org-tree-card--nested'
      } groups-org-tree-card--permanent-merged groups-org-tree-card--${kind}${rootClass}${
        isHover ? ' is-hover' : ''
      }${isCameraFocus ? ' is-camera-focus' : ''}`}
      data-tree-node-id={groupNode.id}
      title="Permanent — view members for this group-period"
      onMouseEnter={() => setHoverId(groupNode.id)}
      onMouseLeave={() => setHoverId((h) => (h === groupNode.id ? null : h))}
      onClick={() => onSelectPeriod?.(periodNode)}
    >
      <span className="groups-org-tree-card-name">{group?.name}</span>
      <span className="groups-org-tree-card-meta">
        <GroupsTypeBadge
          groupType={group?.group_type || 'team'}
          displayLabel={group?.display_label}
        />
        <span className="groups-org-tree-permanent-label" title="Permanent">
        ♾️
        </span>
      </span>
    </button>
  );
}

function ChildRow({ nodes, ghostOpts, renderCell }) {
  const hLineStyle = nodes.length > 1 ? treeHLinePixels(nodes, ghostOpts) : null;
  return (
    <div className="groups-org-tree-connector-block">
      <div className="groups-org-tree-stem-down" aria-hidden />
      <div className="groups-org-tree-child-row">
        {hLineStyle ? (
          <div className="groups-org-tree-h-line" style={hLineStyle} aria-hidden />
        ) : null}
        {nodes.map((child) => {
          const laneMin =
            child.nodeType === 'ghost'
              ? ghostLaneWidth(child)
              : subtreeBranchMinWidth(child, ghostOpts);
          return (
            <div
              key={child.id}
              className={`groups-org-tree-child-cell${
                child.nodeType === 'ghost' ? ' groups-org-tree-child-cell--ghost' : ''
              }${child.mini ? ' groups-org-tree-child-cell--ghost-mini' : ''}`}
              style={{ minWidth: laneMin, width: laneMin }}
            >
              <div
                className={`groups-org-tree-stem-to-card${
                  child.nodeType === 'ghost' ? ' groups-org-tree-stem-to-card--ghost' : ''
                }`}
                aria-hidden
              />
              {renderCell(child)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PeriodTreeBranch({
  node,
  onSelectGroup,
  onSelectPeriod,
  hoverId,
  setHoverId,
  onAddNestedTeam,
  onAddPeriod,
  focusGroupId,
  ghostOpts,
  showAllHierarchy
}) {
  const nested = node.children || [];
  const miniGhost =
    showAllHierarchy && onAddNestedTeam
      ? { id: `ghost-nested-${node.periodGpId}`, nodeType: 'ghost', mini: true }
      : null;
  const folderGhost =
    !showAllHierarchy && ghostOpts?.rowGhosts && onAddNestedTeam
      ? { id: `ghost-nested-${node.periodGpId}`, nodeType: 'ghost' }
      : null;
  const rowNodes = miniGhost
    ? [miniGhost, ...nested]
    : folderGhost
      ? [...nested, folderGhost]
      : nested;

  return (
    <div className="groups-org-tree-branch">
      <div className="groups-org-tree-card-slot">
        <PeriodTreeCard
          node={node}
          onSelectPeriod={onSelectPeriod}
          hoverId={hoverId}
          setHoverId={setHoverId}
        />
      </div>
      {rowNodes.length > 0 ? (
        <ChildRow
          nodes={rowNodes}
          ghostOpts={ghostOpts}
          renderCell={(child) => {
            if (child.nodeType === 'ghost') {
              return (
                <GhostTreeCard
                  id={child.id}
                  label="Nested team"
                  sublabel={`under ${periodLabel(node.period)}`}
                  mini={Boolean(child.mini)}
                  tone="team"
                  onClick={() => onAddNestedTeam(node.periodGpId, node.group)}
                  hoverId={hoverId}
                  setHoverId={setHoverId}
                />
              );
            }
            return (
              <GroupTreeBranch
                node={child}
                onSelectGroup={onSelectGroup}
                onSelectPeriod={onSelectPeriod}
                hoverId={hoverId}
                setHoverId={setHoverId}
                onAddNestedTeam={onAddNestedTeam}
                onAddPeriod={onAddPeriod}
                focusGroupId={focusGroupId}
                ghostOpts={ghostOpts}
                showAllHierarchy={showAllHierarchy}
              />
            );
          }}
        />
      ) : null}
    </div>
  );
}

function GroupTreeBranch({
  node,
  onSelectGroup,
  onSelectPeriod,
  hoverId,
  setHoverId,
  onAddNestedTeam,
  onAddPeriod,
  focusGroupId,
  ghostOpts,
  showAllHierarchy
}) {
  if (node.isDerivedGuardians) {
    return (
      <div className="groups-org-tree-branch">
        <div className="groups-org-tree-card-slot">
          <DerivedGuardiansCard
            node={node}
            onSelectGroup={onSelectGroup}
            hoverId={hoverId}
            setHoverId={setHoverId}
          />
        </div>
      </div>
    );
  }

  const periods = node.children || [];
  const solePermanent =
    ghostOpts?.mergePermanent !== false ? getSolePermanentPeriodChild(node) : null;
  const isFocus =
    showAllHierarchy &&
    focusGroupId != null &&
    node.group?.id != null &&
    String(node.group.id) === String(focusGroupId);

  // Sole Permanent: one card; nested teams hang directly underneath.
  if (solePermanent) {
    const nested = solePermanent.children || [];
    const nestedGhost = onAddNestedTeam
      ? {
          id: `ghost-nested-${solePermanent.periodGpId}`,
          nodeType: 'ghost',
          mini: Boolean(showAllHierarchy)
        }
      : null;
    const rowNodes = nestedGhost
      ? nestedGhost.mini
        ? [nestedGhost, ...nested]
        : [...nested, nestedGhost]
      : nested;

    return (
      <div className={`groups-org-tree-branch${isFocus ? ' is-camera-focus-branch' : ''}`}>
        <div className="groups-org-tree-card-slot">
          <PermanentMergedGroupCard
            groupNode={node}
            periodNode={solePermanent}
            onSelectPeriod={onSelectPeriod}
            hoverId={hoverId}
            setHoverId={setHoverId}
            isCameraFocus={isFocus}
          />
        </div>
        {rowNodes.length > 0 ? (
          <ChildRow
            nodes={rowNodes}
            ghostOpts={ghostOpts}
            renderCell={(child) => {
              if (child.nodeType === 'ghost') {
                return (
                  <GhostTreeCard
                    id={child.id}
                    label="Nested team"
                    sublabel={`under ${node.group?.name || 'this team'}`}
                    mini={Boolean(child.mini)}
                    tone="team"
                    onClick={() =>
                      onAddNestedTeam(solePermanent.periodGpId, solePermanent.group || node.group)
                    }
                    hoverId={hoverId}
                    setHoverId={setHoverId}
                  />
                );
              }
              return (
                <GroupTreeBranch
                  node={child}
                  onSelectGroup={onSelectGroup}
                  onSelectPeriod={onSelectPeriod}
                  hoverId={hoverId}
                  setHoverId={setHoverId}
                  onAddNestedTeam={onAddNestedTeam}
                  onAddPeriod={onAddPeriod}
                  focusGroupId={focusGroupId}
                  ghostOpts={ghostOpts}
                  showAllHierarchy={showAllHierarchy}
                />
              );
            }}
          />
        ) : null}
      </div>
    );
  }

  const canAddAnotherPeriod =
    showAllHierarchy && onAddPeriod && !groupHasPermanentPeriod(node.group);
  const miniGhost = canAddAnotherPeriod
    ? { id: `ghost-period-${node.group?.id || node.id}`, nodeType: 'ghost', mini: true }
    : null;
  const rowNodes = miniGhost ? [miniGhost, ...periods] : periods;

  return (
    <div className={`groups-org-tree-branch${isFocus ? ' is-camera-focus-branch' : ''}`}>
      <div className="groups-org-tree-card-slot">
        <NestedGroupCard
          node={node}
          onSelectGroup={onSelectGroup}
          hoverId={hoverId}
          setHoverId={setHoverId}
          isCameraFocus={isFocus}
          showAllHierarchy={showAllHierarchy}
        />
      </div>
      {rowNodes.length > 0 ? (
        <ChildRow
          nodes={rowNodes}
          ghostOpts={ghostOpts}
          renderCell={(child) => {
            if (child.nodeType === 'ghost') {
              return (
                <GhostTreeCard
                  id={child.id}
                  label="Period"
                  sublabel={`on ${node.group?.name || 'this team'}`}
                  mini={Boolean(child.mini)}
                  tone="period"
                  onClick={() => onAddPeriod(node.group)}
                  hoverId={hoverId}
                  setHoverId={setHoverId}
                />
              );
            }
            return (
              <PeriodTreeBranch
                node={child}
                onSelectGroup={onSelectGroup}
                onSelectPeriod={onSelectPeriod}
                hoverId={hoverId}
                setHoverId={setHoverId}
                onAddNestedTeam={onAddNestedTeam}
                onAddPeriod={onAddPeriod}
                focusGroupId={focusGroupId}
                ghostOpts={ghostOpts}
                showAllHierarchy={showAllHierarchy}
              />
            );
          }}
        />
      ) : null}
    </div>
  );
}

function WebTreeBranch(props) {
  if (props.node.nodeType === 'period') {
    return <PeriodTreeBranch {...props} />;
  }
  return <GroupTreeBranch {...props} />;
}

function GroupsOrgWebLegend() {
  const [expanded, setExpanded] = useState(false);

  return (
    <aside
      className={`groups-org-web-legend${expanded ? ' is-expanded' : ' is-collapsed'}`}
      aria-label="Map legend"
    >
      <button
        type="button"
        className="groups-org-web-legend-toggle"
        aria-expanded={expanded}
        aria-controls="groups-org-web-legend-panel"
        onClick={() => setExpanded((v) => !v)}
      >
        <span>Legend</span>
        <span className="groups-org-web-legend-toggle-caret" aria-hidden>
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded ? (
        <ul id="groups-org-web-legend-panel" className="groups-org-web-legend-list">
          <li>
            <span className="groups-org-web-legend-swatch groups-org-web-legend-swatch--org" aria-hidden />
            Organization
          </li>
          <li>
            <span
              className="groups-org-web-legend-swatch groups-org-web-legend-swatch--anchor"
              aria-hidden
            />
            Anchor team
          </li>
          <li>
            <span
              className="groups-org-web-legend-swatch groups-org-web-legend-swatch--extending"
              aria-hidden
            />
            Extending team
          </li>
          <li>
            <span
              className="groups-org-web-legend-swatch groups-org-web-legend-swatch--period"
              aria-hidden
            />
            Period
          </li>
          <li>
            <span
              className="groups-org-web-legend-mark groups-org-web-legend-mark--permanent"
              aria-hidden
            >
              ♾️
            </span>
            Permanent (embedded on group)
          </li>
          <li>
            <span
              className="groups-org-web-legend-swatch groups-org-web-legend-swatch--derived"
              aria-hidden
            />
            Derived
          </li>
        </ul>
      ) : null}
    </aside>
  );
}

const GroupsOrgWebView = ({
  organization,
  orgGpId = null,
  treeChildren = [],
  showOrgOwnerCrown = false,
  onOpenOrgOwner,
  ownerCrownAriaLabel = 'Open owner team',
  ownerCrownTitle = 'Owner',
  isBrowseRoot = false,
  showAllHierarchy = false,
  onShowAllHierarchyChange,
  loading = false,
  onSelectGroup,
  onSelectPeriod,
  footerSlot = null,
  canAddGroup = false,
  onAddGroup,
  focusGroupId = null,
  cameraNonce = 0
}) => {
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [hoverId, setHoverId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [cameraAnimating, setCameraAnimating] = useState(false);
  const dragRef = useRef(null);
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  const skipNextFitRef = useRef(false);
  const lastCameraKeyRef = useRef('');

  zoomRef.current = zoom;
  panRef.current = pan;

  const solePermanentRoot =
    !showAllHierarchy && isBrowseRoot ? getSolePermanentRootPeriod(treeChildren) : null;

  const ghostOpts = {
    // Folder: full dashed card trailing in the row (shallow trees).
    // Show all: mini ghost first in the row (hugs the stem, ~76px lane).
    rowGhosts: Boolean(!showAllHierarchy && canAddGroup && onAddGroup),
    nestedTeam: Boolean(canAddGroup && onAddGroup),
    miniGhosts: Boolean(showAllHierarchy && canAddGroup && onAddGroup),
    anotherPeriod: Boolean(showAllHierarchy && canAddGroup && onAddGroup),
    // Fold sole Permanent into the group card (folder drill-in + Show all).
    mergePermanent: true
  };

  const fitToView = useCallback(() => {
    const vp = viewportRef.current;
    const content = contentRef.current;
    if (!vp || !content) return;
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    const cw = content.scrollWidth;
    const ch = content.scrollHeight;
    if (vw < 1 || vh < 1 || cw < 1 || ch < 1) return;
    let z = Math.min((vw - CANVAS_PAD * 2) / cw, (vh - CANVAS_PAD * 2) / ch);
    if (!Number.isFinite(z)) return;
    if (z > 1) z = 1;
    z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
    setCameraAnimating(true);
    setZoom(z);
    setPan({ x: (vw - cw * z) / 2, y: (vh - ch * z) / 2 });
    window.setTimeout(() => setCameraAnimating(false), CAMERA_TRANSITION_MS);
  }, []);

  const focusCameraOnNodeId = useCallback((treeNodeId, { zoomTo = FOCUS_ZOOM } = {}) => {
    const vp = viewportRef.current;
    const content = contentRef.current;
    if (!vp || !content || !treeNodeId) return false;
    const el = content.querySelector(
      `[data-tree-node-id="${
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(String(treeNodeId))
          : String(treeNodeId).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      }"]`
    );
    if (!el) return false;

    const currentZoom = zoomRef.current || 1;
    const currentPan = panRef.current || { x: 0, y: 0 };
    const elRect = el.getBoundingClientRect();
    const vpRect = vp.getBoundingClientRect();
    const elCx = elRect.left + elRect.width / 2 - vpRect.left;
    const elCy = elRect.top + elRect.height / 2 - vpRect.top;
    const contentX = (elCx - currentPan.x) / currentZoom;
    const contentY = (elCy - currentPan.y) / currentZoom;

    let z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoomTo));
    // Prefer framing the branch under the focused group when it has children.
    const branch = el.closest('.groups-org-tree-branch');
    if (branch && branch !== content.querySelector('.groups-org-tree-main > .groups-org-tree-branch')) {
      const br = branch.getBoundingClientRect();
      const branchW = br.width / currentZoom;
      const branchH = br.height / currentZoom;
      if (branchW > 0 && branchH > 0) {
        const fitZ = Math.min(
          (vp.clientWidth - CANVAS_PAD * 2) / branchW,
          (vp.clientHeight - CANVAS_PAD * 2) / branchH
        );
        if (Number.isFinite(fitZ) && fitZ > 0) {
          z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(FOCUS_ZOOM, fitZ)));
        }
      }
    }

    setCameraAnimating(true);
    setZoom(z);
    setPan({
      x: vp.clientWidth / 2 - contentX * z,
      y: vp.clientHeight / 2 - contentY * z
    });
    window.setTimeout(() => setCameraAnimating(false), CAMERA_TRANSITION_MS);
    return true;
  }, []);

  useLayoutEffect(() => {
    if (skipNextFitRef.current) {
      skipNextFitRef.current = false;
      return;
    }
    if (showAllHierarchy && focusGroupId) return;
    fitToView();
  }, [fitToView, treeChildren, organization?.id, focusGroupId, showAllHierarchy, isBrowseRoot]);

  useEffect(() => {
    const onResize = () => {
      if (showAllHierarchy && focusGroupId) {
        focusCameraOnNodeId(`group-${focusGroupId}`);
      } else {
        fitToView();
      }
    };
    window.addEventListener('resize', onResize);
    const vp = viewportRef.current;
    let ro;
    if (vp && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => onResize());
      ro.observe(vp);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [fitToView, focusCameraOnNodeId, focusGroupId, showAllHierarchy]);

  // Camera responds to crumb/map focus changes (Show all mode only).
  useEffect(() => {
    if (!showAllHierarchy) {
      lastCameraKeyRef.current = '';
      return;
    }
    const key = `${focusGroupId || ''}:${cameraNonce}`;
    if (key === lastCameraKeyRef.current && cameraNonce === 0 && !focusGroupId) return;
    lastCameraKeyRef.current = key;

    if (!focusGroupId) {
      fitToView();
      return;
    }

    skipNextFitRef.current = true;
    const tryFocus = () => {
      const ok = focusCameraOnNodeId(`group-${focusGroupId}`);
      if (!ok) {
        window.requestAnimationFrame(() => focusCameraOnNodeId(`group-${focusGroupId}`));
      }
    };
    tryFocus();
  }, [focusGroupId, cameraNonce, focusCameraOnNodeId, fitToView, showAllHierarchy]);

  const nudgeZoom = useCallback((delta) => {
    setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z * (1 + delta))));
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      dragRef.current = {
        id: e.pointerId,
        sx: e.clientX,
        sy: e.clientY,
        px: pan.x,
        py: pan.y,
        moved: false
      };
    },
    [pan.x, pan.y]
  );

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) > 5) {
      d.moved = true;
      setIsPanning(true);
      try {
        viewportRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (d.moved) setPan({ x: d.px + dx, y: d.py + dy });
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    dragRef.current = null;
    try {
      viewportRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    setIsPanning(false);
  }, []);

  const orgNode = organization
    ? {
        id: organization.id != null ? `group-${organization.id}` : 'org-root',
        name: organization.name,
        group_type: isBrowseRoot
          ? organization.group_type || 'team'
          : organization.group_type || 'organization',
        group: organization
      }
    : null;

  const canAdd = Boolean(canAddGroup && onAddGroup);
  const displayTreeChildren = solePermanentRoot
    ? solePermanentRoot.children || []
    : treeChildren;

  const rootGhost = canAdd
    ? showAllHierarchy && !isBrowseRoot
      ? {
          id: 'ghost-root-team',
          nodeType: 'ghost',
          mini: true,
          tone: 'team',
          label: 'New team',
          sublabel: `under ${organization?.name || 'organization'}`,
          onClick: () => onAddGroup({})
        }
      : !showAllHierarchy
        ? isBrowseRoot
          ? solePermanentRoot
            ? {
                id: 'ghost-root-nested',
                nodeType: 'ghost',
                tone: 'team',
                label: 'Nested team',
                sublabel: `under ${organization?.name || 'this team'}`,
                onClick: () =>
                  onAddGroup({
                    addMode: 'nested_team',
                    parentAttachGpId: solePermanentRoot.periodGpId,
                    anchorGroup: organization,
                    parentGroupName: organization?.name
                  })
              }
            : groupHasPermanentPeriod(organization)
              ? null
              : {
                  id: 'ghost-root-period',
                  nodeType: 'ghost',
                  tone: 'period',
                  label: 'Another period',
                  sublabel: `on ${organization?.name || 'this team'}`,
                  onClick: () => onAddGroup({ addMode: 'team_period' })
                }
          : {
              id: 'ghost-root-team',
              nodeType: 'ghost',
              tone: 'team',
              label: 'New team',
              sublabel: `under ${organization?.name || 'organization'}`,
              onClick: () => onAddGroup({})
            }
        : null
    : null;

  const handleAddNestedTeam = canAdd
    ? (periodGpId, parentGroup) =>
        onAddGroup({
          addMode: 'nested_team',
          parentAttachGpId: periodGpId,
          anchorGroup: parentGroup || null,
          parentGroupName: parentGroup?.name
        })
    : null;

  const handleAddPeriod =
    canAdd && showAllHierarchy
      ? (group) =>
          onAddGroup({
            addMode: 'team_period',
            anchorGroup: group,
            parentGroupName: group?.name
          })
      : null;

  const rootRowNodes = rootGhost
    ? rootGhost.mini
      ? [rootGhost, ...displayTreeChildren]
      : [...displayTreeChildren, rootGhost]
    : displayTreeChildren;
  const rootHLine =
    rootRowNodes.length > 1 ? treeHLinePixels(rootRowNodes, ghostOpts) : null;

  return (
    <div ref={containerRef} className="groups-org-web">
      <div
        ref={viewportRef}
        className={`groups-org-web-viewport${isPanning ? ' is-panning' : ''}${
          loading ? ' is-loading' : ''
        }`}
        onWheel={(e) => {
          e.preventDefault();
          nudgeZoom((e.deltaY > 0 ? -1 : 1) * 0.08);
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {loading ? (
          <div className="groups-org-web-loading">Loading groups…</div>
        ) : (
          <div
            className={`groups-org-web-canvas${cameraAnimating ? ' is-camera-animating' : ''}`}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
          >
            <div ref={contentRef} className="groups-org-tree-root">
              {orgNode && (
                <div className="groups-org-tree-main">
                  <div className="groups-org-tree-org-slot">
                    {solePermanentRoot ? (
                      <PermanentMergedGroupCard
                        groupNode={orgNode}
                        periodNode={solePermanentRoot}
                        onSelectPeriod={onSelectPeriod}
                        hoverId={hoverId}
                        setHoverId={setHoverId}
                        isRoot
                        isOrganizationRoot={false}
                      />
                    ) : (
                      <GroupTreeCard
                        node={orgNode}
                        isRoot
                        isOrganizationRoot={!isBrowseRoot}
                        onSelectGroup={
                          isBrowseRoot
                            ? undefined
                            : () => {
                                onSelectGroup?.({
                                  nodeType: 'organization',
                                  id: orgNode.id,
                                  group: organization
                                });
                              }
                        }
                        hoverId={hoverId}
                        setHoverId={setHoverId}
                        showOrgOwnerCrown={showOrgOwnerCrown}
                        onOpenOrgOwner={onOpenOrgOwner}
                        ownerCrownAriaLabel={ownerCrownAriaLabel}
                        ownerCrownTitle={ownerCrownTitle}
                      />
                    )}
                  </div>
                  {rootRowNodes.length > 0 ? (
                    <div className="groups-org-tree-connector-block groups-org-tree-connector-block--root">
                      <div className="groups-org-tree-stem-down" aria-hidden />
                      <div className="groups-org-tree-child-row">
                        {rootHLine ? (
                          <div className="groups-org-tree-h-line" style={rootHLine} aria-hidden />
                        ) : null}
                        {rootRowNodes.map((child) => {
                          const isGhost = child.nodeType === 'ghost';
                          const laneMin = isGhost
                            ? ghostLaneWidth(child)
                            : subtreeBranchMinWidth(child, ghostOpts);
                          return (
                            <div
                              key={child.id}
                              className={`groups-org-tree-child-cell${
                                isGhost ? ' groups-org-tree-child-cell--ghost' : ''
                              }${child.mini ? ' groups-org-tree-child-cell--ghost-mini' : ''}`}
                              style={{ minWidth: laneMin, width: laneMin }}
                            >
                              <div
                                className={`groups-org-tree-stem-to-card${
                                  isGhost ? ' groups-org-tree-stem-to-card--ghost' : ''
                                }`}
                                aria-hidden
                              />
                              {isGhost ? (
                                <GhostTreeCard
                                  id={child.id}
                                  label={child.label}
                                  sublabel={child.sublabel}
                                  mini={Boolean(child.mini)}
                                  tone={child.tone || 'team'}
                                  onClick={child.onClick}
                                  hoverId={hoverId}
                                  setHoverId={setHoverId}
                                />
                              ) : (
                                <WebTreeBranch
                                  node={child}
                                  onSelectGroup={onSelectGroup}
                                  onSelectPeriod={onSelectPeriod}
                                  hoverId={hoverId}
                                  setHoverId={setHoverId}
                                  onAddNestedTeam={handleAddNestedTeam}
                                  onAddPeriod={handleAddPeriod}
                                  focusGroupId={focusGroupId}
                                  ghostOpts={ghostOpts}
                                  showAllHierarchy={showAllHierarchy}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : isBrowseRoot ? (
                    <p className="groups-org-web-empty-inline">No periods for this group.</p>
                  ) : !canAddGroup ? (
                    <p className="groups-org-web-empty-inline">No groups in this organization yet.</p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
        {!loading ? <GroupsOrgWebLegend /> : null}
      </div>
      <div className="groups-org-web-footer">
        {footerSlot ? <div className="groups-org-web-footer-slot">{footerSlot}</div> : null}
        <div className="groups-org-web-toolbar">
          <div className="groups-org-web-tool-cluster">
            <button
              type="button"
              className="groups-org-web-tool-btn"
              onClick={() => nudgeZoom(-0.08)}
              title="Zoom out"
            >
              −
            </button>
            <button
              type="button"
              className="groups-org-web-tool-btn groups-org-web-tool-btn--end"
              onClick={() => nudgeZoom(0.08)}
              title="Zoom in"
            >
              +
            </button>
          </div>
          <button type="button" className="groups-org-web-tool-fit" onClick={fitToView} title="Fit map">
            Fit
          </button>
          {typeof onShowAllHierarchyChange === 'function' ? (
            <button
              type="button"
              className={`groups-org-web-tool-fit groups-org-web-tool-show-all${
                showAllHierarchy ? ' is-active' : ''
              }`}
              aria-pressed={showAllHierarchy}
              title={
                showAllHierarchy
                  ? 'Show all hierarchy (on). Click to use folder drill-down.'
                  : 'Show all hierarchy (off). Click to expand the full map.'
              }
              onClick={() => onShowAllHierarchyChange(!showAllHierarchy)}
            >
              Show all
            </button>
          ) : null}
          <span className="groups-org-web-hint">
            {showAllHierarchy
              ? 'Click team to focus · drag to pan · wheel to zoom'
              : 'Click group for periods · drag to pan · wheel to zoom'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GroupsOrgWebView;
